import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Alert, CircularProgress, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AccessPermitForm from '../../components/Permits/AccessPermitForm';
import permitService from '../../services/permitService';

const AccessPermitPage: React.FC = () => {
  console.log('🎯 AccessPermitPage component is rendering!');
  
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  
  console.log('🎯 AccessPermitPage params:', { id });
  
  const isNewPermit = !id || id === 'new';

  // Fetch existing permit data
  const { data: permit, isLoading } = useQuery({
    queryKey: ['permit', id],
    queryFn: () => permitService.getPermit(Number(id!)),
    enabled: !isNewPermit,
  });

  // Save permit mutation
  const savePermitMutation = useMutation({
    mutationFn: (permitData: any) => {
      if (isNewPermit) {
        return permitService.createPermit({ ...permitData, permit_type: 'access' });
      } else {
        return permitService.updatePermit(Number(id!), permitData);
      }
    },
    onSuccess: (savedPermit) => {
      queryClient.invalidateQueries({ queryKey: ['permits'] });
      queryClient.invalidateQueries({ queryKey: ['permit', savedPermit.id] });
      setError(null);
      
      if (isNewPermit) {
        navigate(`/permits/access/${savedPermit.id}`, { replace: true });
      }
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to save permit');
    },
  });

  // PDF generation mutation
  const generatePDFMutation = useMutation({
    mutationFn: () => permitService.generateAccessPermitPDF(id!),
    onSuccess: (pdfBlob) => {
      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Access_Permit_${permit?.work_control_number || id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to generate PDF');
    },
  });

  const handleSave = (permitData: any) => {
    savePermitMutation.mutate(permitData);
  };

  const handleGeneratePDF = () => {
    if (!isNewPermit) {
      generatePDFMutation.mutate();
    } else {
      setError('Please save the permit before generating PDF');
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  console.log('🎯 AccessPermitPage about to render JSX');
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {isNewPermit ? 'New Access Permit' : `Access Permit - ${permit?.work_control_number}`}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {savePermitMutation.isSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Permit saved successfully!
          </Alert>
        )}

        {generatePDFMutation.isSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            PDF generated and downloaded successfully!
          </Alert>
        )}
      </Box>

      <AccessPermitForm
        permitId={id}
        initialData={permit}
        onSave={handleSave}
        onGeneratePDF={handleGeneratePDF}
        readOnly={false}
      />
    </Box>
  );
};

export default AccessPermitPage;